import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import WelcomeModal from '../components/WelcomeModal';
import useCloudSync from '../hooks/useCloudSync';
import realtimeService from '../services/RealtimeService';
import { normalizeEntry, auditEvent } from '../utils/timeClockHelpers';
import { 
  FileText, 
  DollarSign, 
  Users as UsersIcon, 
  TrendingUp, 
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  ClipboardCheck,
  Receipt,
  UserPlus,
  CalendarPlus,
  Play,
  Square,
  MapPin,
  Car,
  ChevronRight
} from 'lucide-react';
import { IconTile, TINT } from '../components/ui/IconTile';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalRevenue: 0,
    activeProjects: 0,
    pendingInvoices: 0,
    monthlyExpenses: 0,
    totalCustomers: 0,
    completedJobs: 0,
    upcomingAppointments: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [greeting, setGreeting] = useState('');
  const [empStats, setEmpStats] = useState({
    activeShift: null,        // { startTime } if currently clocked in
    weekHours: 0,
    weekMiles: 0,
    todayAppts: [],
    pendingInspections: 0,
    lastMileageTrip: null,
  });

  const refreshStats = useCallback(() => {
    const invoices = JSON.parse(localStorage.getItem('maidly_invoices') || '[]');
    const expenses = JSON.parse(localStorage.getItem('maidly_expenses') || '[]');
    const timeEntries = JSON.parse(localStorage.getItem('maidly_timeEntries') || '[]');
    const customers = JSON.parse(localStorage.getItem('maidly_customers') || '[]');
    const appointments = JSON.parse(localStorage.getItem('maidly_appointments') || '[]');

    const revenue = invoices
      .filter(inv => inv.status === 'paid')
      .reduce((sum, inv) => sum + inv.total, 0);

    const pending = invoices.filter(inv => inv.status === 'pending' || inv.status === 'partial').length;
    const active = invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'cancelled').length;
    const completed = invoices.filter(inv => inv.status === 'paid').length;

    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthlyExp = expenses
      .filter(exp => new Date(exp.date) >= monthStart)
      .reduce((sum, exp) => sum + exp.amount, 0);

    // Get upcoming appointments (next 7 days)
    const upcoming = appointments
      .filter(apt => {
        const aptDate = new Date(apt.date);
        return aptDate >= now && aptDate <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 3);

    setUpcomingAppointments(upcoming);

    setStats({
      totalRevenue: revenue,
      activeProjects: active,
      pendingInvoices: pending,
      monthlyExpenses: monthlyExp,
      totalCustomers: customers.length,
      completedJobs: completed,
      upcomingAppointments: upcoming.length
    });

    // Recent activity
    const recentInvoices = invoices.slice(-3).reverse().map(inv => ({
      type: 'invoice',
      icon: Receipt,
      description: `Invoice #${inv.invoiceNumber || inv.id?.slice(0, 8)} - ${inv.customerName}`,
      status: inv.status,
      amount: inv.total,
      time: inv.createdAt || inv.date
    }));

    const recentTime = timeEntries.slice(-3).reverse().map(entry => ({
      type: 'time',
      icon: Clock,
      description: `${entry.employeeName} clocked ${entry.endTime ? 'out' : 'in'}`,
      time: entry.endTime || entry.startTime
    }));

    setRecentActivity([...recentInvoices, ...recentTime].slice(0, 5));

    // ---- Employee-focused stats ----
    try {
      const mileageEntries = JSON.parse(localStorage.getItem('maidly_mileageEntries') || '[]');
      const inspections = JSON.parse(localStorage.getItem('maidly_inspections') || '[]');

      const empKey = (user?.id || '').toString();
      const empEmail = (user?.email || '').toLowerCase();
      const empName = (user?.name || '').toLowerCase();
      const matchesMe = (row) => {
        if (!row) return false;
        if (empKey && row.employeeId && row.employeeId.toString() === empKey) return true;
        if (empEmail && row.employeeEmail && row.employeeEmail.toLowerCase() === empEmail) return true;
        if (empName && row.employeeName && row.employeeName.toLowerCase() === empName) return true;
        return false;
      };

      // Active shift = most recent entry (mine) with no endTime
      const myEntries = timeEntries.filter(matchesMe);
      const active = [...myEntries].reverse().find((e) => !e.endTime);

      // Week hours (Sun-Sat window)
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      weekStart.setHours(0, 0, 0, 0);
      const weekMs = myEntries
        .filter((e) => new Date(e.startTime) >= weekStart)
        .reduce((sum, e) => {
          const end = e.endTime ? new Date(e.endTime).getTime() : Date.now();
          return sum + Math.max(0, end - new Date(e.startTime).getTime());
        }, 0);
      const weekHours = weekMs / 3_600_000;

      // Miles this week (mine)
      const weekMiles = mileageEntries
        .filter((m) => matchesMe(m) && new Date(m.date) >= weekStart)
        .reduce((s, m) => s + (m.miles || 0), 0);

      // Today's appointments (mine or unassigned)
      const todayStart = new Date(now);
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date(todayStart);
      todayEnd.setDate(todayStart.getDate() + 1);
      const todayAppts = appointments
        .filter((a) => {
          const d = new Date(a.date);
          if (!(d >= todayStart && d < todayEnd)) return false;
          if (!a.assignedTo && !a.assignedEmployeeId) return true;
          return matchesMe({ employeeId: a.assignedEmployeeId, employeeName: a.assignedTo });
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 4);

      // Pending inspections assigned to me (excludes completed & approved)
      const pendingInspections = inspections.filter((i) => {
        if (i.status === 'completed' || i.status === 'approved' || i.status === 'archived') return false;
        const assigned = i.assignedEmployees || [];
        if (!assigned.length) return false;
        if (empKey && assigned.includes(empKey)) return true;
        if (empEmail && assigned.some((a) => (a || '').toLowerCase() === empEmail)) return true;
        if (empName && assigned.some((a) => (a || '').toLowerCase() === empName)) return true;
        return false;
      }).length;

      // Last mileage trip for me
      const myTrips = mileageEntries.filter(matchesMe);
      const lastMileageTrip = myTrips.length ? myTrips[myTrips.length - 1] : null;

      setEmpStats({
        activeShift: active || null,
        weekHours,
        weekMiles,
        todayAppts,
        pendingInspections,
        lastMileageTrip,
      });
    } catch (e) {
      // silent
    }
  }, [user]);

  useEffect(() => {
    // Set greeting based on time of day
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 18) setGreeting('Good afternoon');
    else setGreeting('Good evening');
    refreshStats();
  }, [refreshStats]);

  // Tick every 30s so the active-shift timer updates live
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // Realtime: rebuild dashboard stats when other devices push changes
  useCloudSync(refreshStats);

  const isEmployee = user?.role === 'employee';

  // --- Inline Clock In / Clock Out ---------------------------------------
  // Lets an employee toggle their shift straight from the dashboard hero
  // + quick-action tile without opening the Time Clock page. Mirrors the
  // TimeTracking.js semantics (same storage key, same normalize/audit
  // helpers, same GPS capture, same realtime presence broadcast) so
  // both surfaces stay in lockstep and AutoSync picks up the write.
  const captureGpsBestEffort = () =>
    new Promise((resolve) => {
      if (!('geolocation' in navigator)) return resolve(null);
      const timeout = setTimeout(() => resolve(null), 4000);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(timeout);
          resolve({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: Math.round(pos.coords.accuracy || 0),
          });
        },
        () => {
          clearTimeout(timeout);
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 4000, maximumAge: 60000 }
      );
    });

  const [clockBusy, setClockBusy] = useState(false);
  const handleClockToggle = useCallback(async () => {
    if (clockBusy) return;
    setClockBusy(true);
    try {
      const timeEntries = JSON.parse(localStorage.getItem('maidly_timeEntries') || '[]');
      const employees = JSON.parse(localStorage.getItem('maidly_employees') || '[]');

      // Find "me" — same email/id/name matching used by refreshStats above.
      const empKey = (user?.id || '').toString();
      const empEmail = (user?.email || '').toLowerCase();
      const empName = (user?.name || '').toLowerCase();
      const me = employees.find((e) => {
        if (empKey && e.id && e.id.toString() === empKey) return true;
        if (empEmail && (e.email || '').toLowerCase() === empEmail) return true;
        if (empName && (e.name || '').toLowerCase() === empName) return true;
        return false;
      }) || { id: empKey || empEmail || empName, name: user?.name || 'Employee', hourlyRate: 0 };

      const active = timeEntries.find((e) => {
        if (e.endTime) return false;
        if (empKey && e.employeeId && e.employeeId.toString() === empKey) return true;
        if (empEmail && e.employeeEmail && e.employeeEmail.toLowerCase() === empEmail) return true;
        if (empName && e.employeeName && e.employeeName.toLowerCase() === empName) return true;
        return false;
      });

      const gps = await captureGpsBestEffort();

      if (active) {
        // ---- Clock out -----------------------------------------------------
        const now = new Date();
        const start = new Date(active.startTime);
        const breaks = (active.breaks || []).map((b) =>
          b.endTime ? b : { ...b, endTime: now.toISOString() }
        );
        const breakMs = breaks.reduce(
          (acc, b) => acc + (b.endTime ? new Date(b.endTime) - new Date(b.startTime) : 0),
          0
        );
        const grossMs = now.getTime() - start.getTime();
        const netMs = Math.max(0, grossMs - breakMs);
        const hours = parseFloat((netMs / 3_600_000).toFixed(2));
        const rate = parseFloat(active.hourlyRate || 0);
        const pay = parseFloat((hours * rate).toFixed(2));

        const next = timeEntries.map((e) =>
          e.id === active.id
            ? {
                ...e,
                endTime: now.toISOString(),
                breaks,
                hours,
                pay,
                gpsClockOut: gps,
                auditLog: [...(e.auditLog || []), auditEvent(user, 'clocked-out')],
              }
            : e
        );
        localStorage.setItem('maidly_timeEntries', JSON.stringify(next));
        realtimeService.setStatus?.('online');
        toast.success(`Clocked out — ${hours.toFixed(2)}h`);
      } else {
        // ---- Clock in ------------------------------------------------------
        const entry = normalizeEntry({
          id: `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          employeeId: me.id,
          employeeName: me.name,
          employeeEmail: user?.email || null,
          startTime: new Date().toISOString(),
          endTime: null,
          hours: 0,
          pay: 0,
          hourlyRate: parseFloat(me.hourlyRate) || 0,
          note: '',
          project: 'General Work',
          task: 'Regular Hours',
          job: '',
          gpsClockIn: gps,
          gpsClockOut: null,
          breaks: [],
          auditLog: [auditEvent(user, 'created')],
        });
        const next = [...timeEntries, entry];
        localStorage.setItem('maidly_timeEntries', JSON.stringify(next));
        realtimeService.setStatus?.('working');
        toast.success(`Clocked in${gps ? ' (GPS verified)' : ''}`);
      }

      refreshStats();
    } catch (e) {
      toast.error('Could not update your shift. Please try again.');
    } finally {
      setClockBusy(false);
    }
  }, [clockBusy, user, refreshStats]);

  const quickActions = [
    { icon: Plus, label: 'New Invoice', path: '/invoices', color: '#22c55e' },
    { icon: UserPlus, label: 'Add Customer', path: '/customers', color: '#ec4899' },
    { icon: CalendarPlus, label: 'Schedule', path: '/scheduling', color: '#f97316' },
    { icon: ClipboardCheck, label: 'Inspection', path: '/inspections', color: '#10b981' }
  ];

  if (isEmployee) {
    const fmtHours = (h) => {
      const mins = Math.round(h * 60);
      const hrs = Math.floor(mins / 60);
      const rem = mins % 60;
      return `${hrs}h ${rem}m`;
    };
    const shiftElapsed = empStats.activeShift
      ? fmtHours(
          (Date.now() - new Date(empStats.activeShift.startTime).getTime()) / 3_600_000
        )
      : null;

    return (
      <div data-testid="employee-dashboard" style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <WelcomeModal user={user} />

        {/* Greeting */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.25rem' }}>
            {greeting}, {user?.name || 'Team Member'}!
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.95rem' }}>
            {empStats.todayAppts.length > 0
              ? `You have ${empStats.todayAppts.length} job${empStats.todayAppts.length > 1 ? 's' : ''} today. Let's go!`
              : 'Ready to make today productive?'}
          </p>
        </div>

        {/* Hero: Active Shift OR Clock-In CTA */}
        {empStats.activeShift ? (
          <button
            onClick={handleClockToggle}
            disabled={clockBusy}
            data-testid="emp-shift-active-card"
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '1.5rem',
              marginBottom: '1.25rem',
              borderRadius: 20,
              border: 'none',
              cursor: clockBusy ? 'wait' : 'pointer',
              opacity: clockBusy ? 0.75 : 1,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#fff',
              boxShadow: '0 12px 28px rgba(16,185,129,0.35)',
              display: 'flex',
              alignItems: 'center',
              gap: '1.25rem',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at 90% 20%, rgba(255,255,255,0.15), transparent 50%)',
                pointerEvents: 'none',
              }}
            />
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '1.25rem', flex: 1, minWidth: 0 }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 16,
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 6px 18px rgba(0,0,0,0.15) inset',
                }}
              >
                <Clock size={30} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 0 0 4px rgba(255,255,255,0.25)',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  />
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', opacity: 0.9 }}>
                    On the clock
                  </span>
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1, marginBottom: 6 }}>
                  {shiftElapsed}
                </div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                  Started at{' '}
                  {new Date(empStats.activeShift.startTime).toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                  {clockBusy ? ' • Clocking out…' : ' • Tap to clock out'}
                </div>
              </div>
            </div>
            <ChevronRight size={28} style={{ opacity: 0.8, flexShrink: 0 }} />
          </button>
        ) : (
          <button
            onClick={handleClockToggle}
            disabled={clockBusy}
            data-testid="emp-clock-in-card"
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '1.5rem',
              marginBottom: '1.25rem',
              borderRadius: 20,
              border: 'none',
              cursor: clockBusy ? 'wait' : 'pointer',
              opacity: clockBusy ? 0.75 : 1,
              background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
              color: '#fff',
              boxShadow: '0 12px 28px rgba(0,115,234,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '1.25rem',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at 85% 15%, rgba(255,255,255,0.18), transparent 55%)',
                pointerEvents: 'none',
              }}
            />
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '1.25rem', flex: 1 }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 16,
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Play size={26} fill="#fff" />
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', opacity: 0.85, marginBottom: 4 }}>
                  Not on the clock
                </div>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, lineHeight: 1.1, marginBottom: 6 }}>
                  Start your shift
                </div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                  {clockBusy ? 'Clocking in…' : 'Tap to clock in and begin tracking hours'}
                </div>
              </div>
            </div>
            <ChevronRight size={28} style={{ opacity: 0.85, flexShrink: 0 }} />
          </button>
        )}

        {/* Stat tiles: hours + miles + jobs + inspections */}
        <div
          data-testid="emp-stats-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))',
            gap: '0.85rem',
            marginBottom: '1.25rem',
          }}
        >
          <StatMini
            tint={TINT.timeClock}
            icon={Clock}
            label="Hours this week"
            value={fmtHours(empStats.weekHours)}
          />
          <StatMini
            tint={TINT.scheduling}
            icon={Calendar}
            label="Jobs today"
            value={empStats.todayAppts.length}
          />
          <StatMini
            tint={TINT.mileage}
            icon={Car}
            label="Miles this week"
            value={empStats.weekMiles.toFixed(1)}
          />
          <StatMini
            tint={TINT.inspections}
            icon={ClipboardCheck}
            label="Open inspections"
            value={empStats.pendingInspections}
          />
        </div>

        {/* Today's Schedule */}
        <div
          className="card"
          style={{ padding: 0, marginBottom: '1.25rem', overflow: 'hidden' }}
          data-testid="emp-today-schedule"
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.25rem',
              borderBottom: '1px solid #f1f5f9',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <IconTile icon={Calendar} tint={TINT.scheduling} size={34} />
              <div>
                <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '1rem' }}>Today&apos;s Schedule</div>
                <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
                  {empStats.todayAppts.length} appointment{empStats.todayAppts.length === 1 ? '' : 's'}
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate('/scheduling')}
              data-testid="emp-view-all-schedule"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '0.4rem 0.8rem',
                border: '1px solid #fed7aa',
                background: '#fff7ed',
                color: '#c2410c',
                borderRadius: 999,
                fontSize: '0.78rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              View all <ChevronRight size={14} />
            </button>
          </div>

          {empStats.todayAppts.length === 0 ? (
            <div style={{ padding: '2rem 1.5rem', textAlign: 'center', color: '#94a3b8' }}>
              <Calendar size={32} style={{ margin: '0 auto 0.5rem', opacity: 0.4 }} />
              <div style={{ fontWeight: 500 }}>No appointments today</div>
              <div style={{ fontSize: '0.8rem' }}>Enjoy the day off, or check the schedule for later this week.</div>
            </div>
          ) : (
            <div>
              {empStats.todayAppts.map((apt, i) => (
                <div
                  key={apt.id || i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.9rem',
                    padding: '0.85rem 1.25rem',
                    borderBottom: i < empStats.todayAppts.length - 1 ? '1px solid #f1f5f9' : 'none',
                  }}
                >
                  <div
                    style={{
                      minWidth: 62,
                      padding: '0.5rem 0.6rem',
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, #fff7ed, #ffedd5)',
                      color: '#9a3412',
                      textAlign: 'center',
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      lineHeight: 1.15,
                    }}
                  >
                    {new Date(apt.date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {apt.customerName || apt.title || 'Appointment'}
                    </div>
                    {(apt.address || apt.location) && (
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <MapPin size={12} />
                        {apt.address || apt.location}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div style={{ marginBottom: '0.5rem', fontWeight: 700, color: '#475569', fontSize: '0.85rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Quick Actions
        </div>
        <div
          data-testid="emp-quick-actions"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))',
            gap: '0.85rem',
          }}
        >
          <QuickAction
            testId="emp-action-timeclock"
            onClick={handleClockToggle}
            tint={TINT.timeClock}
            icon={empStats.activeShift ? Square : Play}
            title={empStats.activeShift ? 'Clock Out' : 'Clock In'}
            subtitle={clockBusy ? 'Working…' : 'Track hours'}
          />
          <QuickAction
            testId="emp-action-mileage"
            onClick={() => navigate('/mileage')}
            tint={TINT.mileage}
            icon={Car}
            title="Log a Drive"
            subtitle="GPS or manual"
          />
          <QuickAction
            testId="emp-action-inspections"
            onClick={() => navigate('/inspections')}
            tint={TINT.inspections}
            icon={ClipboardCheck}
            title="Inspections"
            subtitle={
              empStats.pendingInspections
                ? `${empStats.pendingInspections} pending`
                : 'Start a new one'
            }
            badge={empStats.pendingInspections || null}
          />
          <QuickAction
            testId="emp-action-schedule"
            onClick={() => navigate('/scheduling')}
            tint={TINT.scheduling}
            icon={Calendar}
            title="My Schedule"
            subtitle="View jobs"
          />
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50%      { transform: scale(1.3); opacity: 0.6; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div data-testid="dashboard">
      <WelcomeModal user={user} />
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.5rem' }}>
          {greeting}, {user?.name || 'there'}!
        </h1>
        <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>Here&apos;s what&apos;s happening with your business today.</p>
      </div>

      {/* Quick Actions */}
      <div className="dashboard-quick-actions" style={{ 
        display: 'grid', 
        gap: '1rem', 
        marginBottom: '2rem' 
      }}>
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.path}
              onClick={() => navigate(action.path)}
              data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g,'-')}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.6rem',
                padding: '1.1rem 1rem',
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '14px',
                cursor: 'pointer',
                transition: 'background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = `0 10px 24px ${action.color}33`;
                e.currentTarget.style.background = `${action.color}0d`;
                e.currentTarget.style.borderColor = `${action.color}66`;
                const label = e.currentTarget.querySelector('[data-role="qa-label"]');
                if (label) label.style.color = action.color;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.borderColor = '#e5e7eb';
                const label = e.currentTarget.querySelector('[data-role="qa-label"]');
                if (label) label.style.color = '#374151';
              }}
            >
              <IconTile icon={Icon} tint={action.color} size={44} />
              <span data-role="qa-label" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', transition: 'color 0.2s ease' }}>
                {action.label}
              </span>
            </button>
          );
        })}
      </div>
      
      {/* Stats Grid */}
      <div className="dashboard-stats-grid" style={{ 
        display: 'grid', 
        gap: '1rem', 
        marginBottom: '2rem' 
      }}>
        {/* Revenue Card */}
        <div style={{ 
          background: 'linear-gradient(135deg, #0073ea 0%, #005bb5 100%)',
          borderRadius: '16px',
          padding: '1.25rem',
          color: '#fff'
        }} data-testid="revenue-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <DollarSign size={20} />
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px', 
              fontSize: '0.75rem',
              background: 'rgba(255,255,255,0.2)',
              padding: '4px 8px',
              borderRadius: '20px'
            }}>
              <ArrowUpRight size={14} />
              <span>12%</span>
            </div>
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.25rem' }}>
            ${stats.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>Total Revenue</div>
        </div>
        
        {/* Customers Card */}
        <div style={{ 
          background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
          borderRadius: '16px',
          padding: '1.25rem',
          color: '#fff'
        }} data-testid="customers-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <UsersIcon size={20} />
            </div>
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.25rem' }}>
            {stats.totalCustomers}
          </div>
          <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>Total Customers</div>
        </div>
        
        {/* Pending Invoices */}
        <div style={{ 
          background: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
          borderRadius: '16px',
          padding: '1.25rem',
          color: '#fff'
        }} data-testid="invoices-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FileText size={20} />
            </div>
            {stats.pendingInvoices > 0 && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px', 
                fontSize: '0.75rem',
                background: 'rgba(255,255,255,0.2)',
                padding: '4px 8px',
                borderRadius: '20px'
              }}>
                <AlertCircle size={14} />
                <span>Action needed</span>
              </div>
            )}
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.25rem' }}>
            {stats.pendingInvoices}
          </div>
          <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>Pending Invoices</div>
        </div>
        
        {/* Expenses Card */}
        <div style={{ 
          background: 'linear-gradient(135deg, #1e3a5f 0%, #0d2137 100%)',
          borderRadius: '16px',
          padding: '1.25rem',
          color: '#fff'
        }} data-testid="expenses-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <TrendingUp size={20} />
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px', 
              fontSize: '0.75rem',
              background: 'rgba(255,255,255,0.2)',
              padding: '4px 8px',
              borderRadius: '20px'
            }}>
              <ArrowDownRight size={14} />
              <span>This month</span>
            </div>
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.25rem' }}>
            ${stats.monthlyExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>Monthly Expenses</div>
        </div>
      </div>

      {/* Bottom Section: Activity + Upcoming */}
      <div className="dashboard-bottom-grid" style={{ display: 'grid', gap: '1.5rem' }}>
        {/* Recent Activity */}
        <div style={{ 
          background: '#fff',
          borderRadius: '16px',
          padding: '1.5rem',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1f2937', margin: 0 }}>Recent Activity</h2>
            <button 
              onClick={() => navigate('/invoices')}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#0073ea', 
                fontSize: '0.8rem', 
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              View all →
            </button>
          </div>
          
          {recentActivity.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '2rem',
              color: '#9ca3af'
            }}>
              <Clock size={32} style={{ marginBottom: '0.75rem', opacity: 0.5 }} />
              <p style={{ margin: 0, fontSize: '0.9rem' }}>No recent activity</p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem' }}>Start by creating an invoice or adding a customer</p>
            </div>
          ) : (
            <div>
              {recentActivity.map((activity, index) => {
                const Icon = activity.icon || Clock;
                const tintByType = {
                  invoice:     '#22c55e',
                  customer:    '#ec4899',
                  clock:       '#3b82f6',
                  timeclock:   '#3b82f6',
                  expense:     '#ef4444',
                  inspection:  '#10b981',
                  appointment: '#f97316',
                  schedule:    '#f97316',
                  employee:    '#a855f7',
                };
                const activityTint = tintByType[activity.type] || '#64748b';
                return (
                  <div 
                    key={activity.id || `${activity.type || ''}-${activity.date || activity.created_at || ''}`}
                    style={{ 
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      padding: '0.75rem 0', 
                      borderBottom: index < recentActivity.length - 1 ? '1px solid #f3f4f6' : 'none' 
                    }}
                  >
                    <IconTile icon={Icon} tint={activityTint} size={34} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem' }}>
                        {activity.description}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                        {activity.time ? new Date(activity.time).toLocaleDateString() : 'Recently'}
                        {activity.amount && ` • $${activity.amount.toFixed(2)}`}
                      </div>
                    </div>
                    {activity.status && (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        background: activity.status === 'paid' ? '#dcfce7' : activity.status === 'pending' ? '#fef3c7' : '#fee2e2',
                        color: activity.status === 'paid' ? '#166534' : activity.status === 'pending' ? '#92400e' : '#dc2626'
                      }}>
                        {activity.status}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Upcoming Appointments */}
        <div style={{ 
          background: '#fff',
          borderRadius: '16px',
          padding: '1.5rem',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1f2937', margin: 0 }}>Upcoming Schedule</h2>
            <button 
              onClick={() => navigate('/scheduling')}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#0073ea', 
                fontSize: '0.8rem', 
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              View all →
            </button>
          </div>
          
          {upcomingAppointments.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '2rem',
              color: '#9ca3af'
            }}>
              <Calendar size={32} style={{ marginBottom: '0.75rem', opacity: 0.5 }} />
              <p style={{ margin: 0, fontSize: '0.9rem' }}>No upcoming appointments</p>
              <button 
                onClick={() => navigate('/scheduling')}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  background: 'linear-gradient(135deg, #0073ea 0%, #1e3a5f 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                <CalendarPlus size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                Schedule Appointment
              </button>
            </div>
          ) : (
            <div>
              {upcomingAppointments.map((apt, index) => (
                <div 
                  key={apt.id || `${apt.date || ''}-${apt.startTime || ''}-${apt.customerName || ''}`}
                  style={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '0.75rem 0', 
                    borderBottom: index < upcomingAppointments.length - 1 ? '1px solid #f3f4f6' : 'none' 
                  }}
                >
                  <div style={{
                    width: '40px',
                    textAlign: 'center',
                    background: '#f3f4f6',
                    borderRadius: '8px',
                    padding: '0.5rem 0',
                    flexShrink: 0
                  }}>
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase' }}>
                      {new Date(apt.date).toLocaleDateString('en-US', { month: 'short' })}
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937' }}>
                      {new Date(apt.date).getDate()}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>
                      {apt.customerName || apt.title || 'Appointment'}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Clock size={12} />
                      {apt.time || 'Time TBD'}
                      {apt.serviceType && ` • ${apt.serviceType}`}
                    </div>
                  </div>
                  <CheckCircle2 size={18} color="#10b981" style={{ opacity: 0.5 }} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Small helper components used by the Employee dashboard ----

const StatMini = ({ tint, icon: Icon, label, value }) => (
  <div
    style={{
      background: '#fff',
      border: '1px solid #eef2f7',
      borderRadius: 14,
      padding: '0.9rem 1rem',
      boxShadow: '0 1px 2px rgba(15,23,42,0.03)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    }}
  >
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        background: `${tint}18`,
        color: tint,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon size={20} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 4, fontWeight: 500 }}>{label}</div>
    </div>
  </div>
);

const QuickAction = ({ tint, icon: Icon, title, subtitle, onClick, badge, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    style={{
      textAlign: 'left',
      background: '#fff',
      border: '1px solid #eef2f7',
      borderRadius: 16,
      padding: '1rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.85rem',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
      position: 'relative',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = `0 10px 24px ${tint}22`;
      e.currentTarget.style.borderColor = `${tint}55`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
      e.currentTarget.style.borderColor = '#eef2f7';
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: 12,
        background: `linear-gradient(135deg, ${tint} 0%, ${tint}cc 100%)`,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: `0 6px 14px ${tint}44`,
      }}
    >
      <Icon size={20} />
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>{title}</div>
      <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{subtitle}</div>
    </div>
    {badge ? (
      <span
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          background: '#ef4444',
          color: '#fff',
          borderRadius: 999,
          padding: '2px 8px',
          fontSize: '0.7rem',
          fontWeight: 700,
          boxShadow: '0 2px 6px rgba(239,68,68,0.45)',
        }}
      >
        {badge}
      </span>
    ) : null}
  </button>
);

export default Dashboard;
